import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { User } from './authService';

export type StaffTodo = Tables<'staff_todos'>;

export const getStaffTodos = async (clinicId?: string, user?: User | null): Promise<StaffTodo[]> => {
  const query = supabase
    .from('staff_todos')
    .select(`
      *,
      project:projects(
        id,
        name,
        pm_staff_id,
        project_staff(
          staff_id,
          role
        )
      )
    `)
    .order('created_at', { ascending: false });

  // Note: clinicId parameter kept for compatibility but no longer used for filtering
  // Tasks are now visible across all clinics

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching staff todos:', error);
    throw error;
  }

  let todos = data || [];

  // Apply permission-based filtering on the client side
  if (user) {
    // Admin and Office Admin can see all tasks
    if (user.role === 'admin' || user.role === 'office_admin') {
      return todos;
    }

    // Operations Manager and Office User can only see their own tasks
    // Check if the user has a linked staff member
    let userStaffId: string | undefined = user.staffId;
    
    // If staffId is not in the user object, try to fetch it from the database
    if (!userStaffId) {
      const { data: staffData } = await supabase
        .from('staff')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (staffData) {
        userStaffId = staffData.id;
      }
    }

    if (userStaffId) {
      
      // Filter tasks based on permissions
      todos = todos.filter(todo => {
        // User's own tasks
        if (todo.staff_id === userStaffId) {
          return true;
        }

        // Tasks from projects where user is a member or PM
        if (todo.project_id) {
          // Check if user is the PM
          const project = (todo as any).project;
          if (project && project.pm_staff_id === userStaffId) {
            return true;
          }

          // Check if user is a project member
          if (project && project.project_staff) {
            const isProjectMember = project.project_staff.some(
              (ps: any) => ps.staff_id === userStaffId
            );
            if (isProjectMember) {
              return true;
            }
          }
        }

        return false;
      });
    } else {
      // If no staff record found for this user, return empty array
      // (except for admin and office_admin which are already handled above)
      return [];
    }
  }

  return todos;
};

export const createStaffTodo = async (todo: Omit<StaffTodo, 'id' | 'created_at' | 'updated_at'>): Promise<StaffTodo> => {
  const { data, error } = await supabase
    .from('staff_todos')
    .insert([todo])
    .select()
    .single();

  if (error) {
    console.error('Error creating staff todo:', error);
    throw error;
  }

  return data;
};

export const updateStaffTodo = async (id: string, updates: Partial<StaffTodo>): Promise<StaffTodo> => {
  const { data, error } = await supabase
    .from('staff_todos')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating staff todo:', error);
    throw error;
  }

  return data;
};

export const deleteStaffTodo = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('staff_todos')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting staff todo:', error);
    throw error;
  }
};

/**
 * Get routine tasks for a specific month, filtered by staff
 * @param year - Target year (e.g., 2025)
 * @param month - Target month (1-12)
 * @param staffId - Staff ID to filter tasks (each staff can only duplicate their own routine tasks)
 * @returns Array of routine tasks from the specified month belonging to the staff member
 */
export const getRoutineTasksByMonth = async (
  year: number,
  month: number,
  staffId?: string
): Promise<StaffTodo[]> => {
  // Calculate date range for the month
  const startDate = new Date(year, month - 1, 1); // month is 1-indexed, Date constructor is 0-indexed
  const endDate = new Date(year, month, 0, 23, 59, 59, 999); // Last day of month

  let query = supabase
    .from('staff_todos')
    .select('*')
    .eq('task_type', 'Routine')
    .gte('deadline', startDate.toISOString())
    .lte('deadline', endDate.toISOString())
    .order('deadline', { ascending: true });

  // Filter by staff_id - each staff can only duplicate their own routine tasks
  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching routine tasks by month:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get all tasks for a specific month, filtered by staff
 * Returns both Routine and Ad-hoc tasks for selection-based duplication
 */
export const getAllTasksByMonth = async (
  year: number,
  month: number,
  staffId?: string
): Promise<StaffTodo[]> => {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59, 999);

  let query = supabase
    .from('staff_todos')
    .select('*')
    .gte('deadline', startDate.toISOString())
    .lte('deadline', endDate.toISOString())
    .order('deadline', { ascending: true });

  if (staffId) {
    query = query.eq('staff_id', staffId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching tasks by month:', error);
    throw error;
  }

  return data || [];
};

/**
 * Duplicate routine tasks to a new month, adjusting dates appropriately
 * @param sourceTasks - Array of routine tasks to duplicate
 * @param targetYear - Year for the new tasks
 * @param targetMonth - Month for the new tasks (1-12)
 * @returns Array of newly created tasks
 */
export const duplicateRoutineTasksToMonth = async (
  sourceTasks: StaffTodo[],
  targetYear: number,
  targetMonth: number
): Promise<StaffTodo[]> => {
  if (sourceTasks.length === 0) {
    return [];
  }

  // Create new tasks with adjusted dates
  const lastDayOfTargetMonth = new Date(targetYear, targetMonth, 0).getDate();

  const newTasks = sourceTasks.map(task => {
    const sourceDueDate = new Date(task.deadline);
    const dayOfMonth = sourceDueDate.getDate();

    // Create new due date in target month (preserve day of month if possible)
    const targetDay = Math.min(dayOfMonth, lastDayOfTargetMonth);
    const newDueDate = new Date(targetYear, targetMonth - 1, targetDay);

    // Adjust start_date to target month if it exists
    let newStartDate: string | null = null;
    if (task.start_date) {
      const sourceStartDate = new Date(task.start_date);
      const startDayOfMonth = sourceStartDate.getDate();
      const targetStartDay = Math.min(startDayOfMonth, lastDayOfTargetMonth);
      newStartDate = new Date(targetYear, targetMonth - 1, targetStartDay).toISOString();
    }

    // Prepare task data, excluding system fields
    const { id, created_at, updated_at, ...taskData } = task;

    return {
      ...taskData,
      deadline: newDueDate.toISOString(),
      start_date: newStartDate,
      status: 'pending' as const, // Reset status for new tasks
      completed_at: null,
      rating: null,
      reviewer_comment: null,
      reviewer_id: null,
      reviewed_at: null,
      late_reason: null
    };
  });

  // Bulk insert new tasks
  const { data, error } = await supabase
    .from('staff_todos')
    .insert(newTasks)
    .select();

  if (error) {
    console.error('Error duplicating routine tasks:', error);
    throw error;
  }

  return data || [];
};